import React, { useEffect, useRef, useState, useMemo } from "react";
import { Box, Grid, InputLabel, useMediaQuery } from "@mui/material";
import AvatarPanel from "./AvatarPanel.tsx";
import CustomReactMarkDown from "../../utils/markdown-wrapper.tsx";
import FavouritePrompt from "./FavouritePrompt.tsx";
import ExplainPrompt from "./ExplainPrompt.tsx";
import ThumbUpPrompt from "./ThumbUpPrompt.tsx";
import ThumbDownPrompt from "./ThumbDownPrompt.tsx";
import TextToSpeechButton from "./TextToSpeechButton.tsx";
import ErrorBoundary from "./ErrorBoundary.tsx";
import ProgressMessage from "./ProgressMessage.tsx";
import { useTypedSelector } from "../../hooks.tsx";
import theme from "../../theme.tsx";
import { useTypingEffect } from "../../hooks/useTypingEffect.ts";
import { useFeatureFlag } from "../../hooks/useFeatureFlag.tsx";

interface PromptPanelProps {
  children?: React.ReactNode;
  content: string;
  customContent?: React.ReactNode;
  role: string;
  exchange_id?: string;
  feedback?: string | null;
  user_comment?: string | null;
  progress_update?: boolean | null;
  isStreaming?: boolean;
  onPulseAnimation?: () => void;
  stopPulseAnimation?: () => void;
}

const PromptPanel: React.FC<PromptPanelProps> = (props) => {
  const [favourite, setFavourite] = useState(false);
  const [displayedContent, setDisplayedContent] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLoggedIn = useTypedSelector((state) => state.authState.isLoggedIn);
  const { anonymousSession } = useTypedSelector((state) => state.authState);
  const displayFeedbackButtons = !(
    anonymousSession &&
    anonymousSession.signUpState &&
    !anonymousSession.signUpState.isComplete
  );
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  // Check if this is a custom action response (negative ID indicates Save Favourite, Save Memory, etc.)
  const isCustomActionResponse =
    props.exchange_id && props.exchange_id.startsWith("-");
  const isVoiceAssistantEnabled = useFeatureFlag("voice_assistant");

  useEffect(() => {
    // Clean up the timeout when the component is about to be unmounted
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const { displayed, typing } = useTypingEffect(
    props.content,
    !!props.isStreaming,
    { htmlAware: true, minDelayMs: 40, randomRangeMs: 60 },
  );

  useEffect(() => {
    setIsStreaming(typing);
    setDisplayedContent(displayed);
  }, [typing, displayed]);

  // Memoize expensive style calculations
  const containerStyles = useMemo(
    () => ({
      whiteSpace: "normal" as const,
      backgroundColor: props.role === "user" ? "white" : "transparent",
      color: props.role === "user" ? "black" : "inherit",
      borderRadius: props.role === "user" ? "15px" : "0",
      padding: props.role === "user" ? "0px 15px" : "0",
      maxWidth: props.role === "user" ? "100%" : "100%",
      display: "inline-block",
      marginLeft: props.role === "user" ? "20%" : "0%",
    }),
    [props.role, isSmallScreen],
  );

  // Memoize content to display
  const finalContentToDisplay = useMemo(() => {
    return props.isStreaming ? displayedContent : props.content;
  }, [props.isStreaming, displayedContent, props.content]);

  return (
    <Box marginBottom={"4%"}>
      <Grid
        container
        direction="row"
        spacing={2}
        alignContent="flex-start"
        alignItems="flex-start"
      >
        <Grid
          size={{ xs: 1, lg: 1 }}
          sx={{
            marginTop: "1%",
          }}
        >
          {/* Assistant's Avatar */}
          {props.role !== "user" && (
            <Grid
              size={{ xs: 1, lg: 1 }}
              sx={{
                marginTop: { xs: "60%", md: "0%", lg: "5%", xl: "0%" },
              }}
            >
              <AvatarPanel />
            </Grid>
          )}
        </Grid>
        <Grid size={{ xs: 11, lg: 11 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: props.role === "user" ? "row-reverse" : "row",
              justifyContent:
                props.role === "user" ? "flex-start" : "flex-start",
            }}
          >
            <Box sx={containerStyles}>
              <InputLabel
                sx={{
                  color: "black",
                  whiteSpace: "normal",
                }}
              >
                <ErrorBoundary>
                  <ProgressMessage progressUpdate={!!props.progress_update}>
                    <CustomReactMarkDown
                      content={finalContentToDisplay}
                      role={props.role}
                    ></CustomReactMarkDown>
                    {!isStreaming && (
                      <Box sx={{ marginTop: "12px" }}>
                        {props.customContent}
                      </Box>
                    )}
                  </ProgressMessage>
                </ErrorBoundary>
              </InputLabel>
            </Box>
          </Box>
        </Grid>
      </Grid>
      {props.role === "user" ? (
        <Grid
          position={"relative"}
          sx={{
            marginBottom: "2%",
            marginTop: "1%",
          }}
        >
          <Grid
            container
            sx={{
              display: "flex",
              direction: "row",
              justifyContent: "flex-end",
              marginBottom: "1%",
            }}
          >
            {isLoggedIn && (
              <>
                <Grid>
                  <FavouritePrompt
                    content={props.content}
                    clicked={favourite}
                    setClicked={setFavourite}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      ) : (
        props.role !== "user" &&
        displayFeedbackButtons &&
        !isCustomActionResponse && (
          <Grid
            position={"relative"}
            sx={{ marginLeft: "8%", marginBottom: "6%" }}
          >
            <Grid container direction="row" position={"absolute"}>
              {props.feedback !== "bad" && (
                <Grid>
                  <ThumbUpPrompt
                    exchange_id={props.exchange_id}
                    clicked={props.feedback === "good"}
                  />
                </Grid>
              )}
              {props.feedback !== "good" && (
                <Grid>
                  <ThumbDownPrompt
                    exchange_id={props.exchange_id}
                    clicked={props.feedback === "bad"}
                    onPulseAnimation={props.onPulseAnimation}
                    stopPulseAnimation={props.stopPulseAnimation}
                  />
                </Grid>
              )}
              <Grid>
                {isLoggedIn && (
                  <ExplainPrompt exchange_id={props.exchange_id} />
                )}
              </Grid>
              <Grid>
                {isLoggedIn && isVoiceAssistantEnabled && (
                  <TextToSpeechButton
                    text={displayedContent}
                    exchange_id={props.exchange_id}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
        )
      )}
    </Box>
  );
};

// Custom comparison function for React.memo
const areEqual = (
  prevProps: PromptPanelProps,
  nextProps: PromptPanelProps,
): boolean => {
  // Check if all props are the same
  const keys = Object.keys(nextProps) as Array<keyof PromptPanelProps>;

  for (const key of keys) {
    if (prevProps[key] !== nextProps[key]) {
      return false; // Props are different, component should re-render
    }
  }

  return true; // Props are the same, prevent re-render
};

export default React.memo(PromptPanel, areEqual);
